"use client"
import { Routes } from "@/lib/routes"
import { ArrowRight, CheckCircle, Star, Users } from "lucide-react"
import React, { lazy } from "react"

const OptimizedImage = lazy(() => import("@/components/ui/Image/OptimizedImage"))

const Hero = () => {
   const [isSubmitted, setIsSubmitted] = React.useState(false)

   const [email, setEmail] = React.useState("")

   const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      setIsSubmitted(true)
      // Simulate a redirect or further action
      setTimeout(() => {
         window.location.href = Routes.client.unifiedSignupEmail.getPath(email)
      }, 2000)
   }

   return (
      <section className="relative overflow-hidden bg-orange-50 pb-16 pt-20">
         {/* Background Pattern */}
         {/* <div className="absolute inset-0 opacity-5">
            <div className="bg-orange-500 absolute left-10 top-10 h-20 w-20 rounded-full"></div>
            <div className="bg-orange-400 absolute right-20 top-32 h-16 w-16 rounded-full"></div>
            <div className="bg-orange-400 absolute bottom-20 left-1/4 h-12 w-12 rounded-full"></div>
            <div className="bg-orange-300 absolute bottom-32 right-1/3 h-24 w-24 rounded-full"></div>
         </div> */}
         <div className="mx-auto mt-[98px] max-w-7xl px-4 sm:px-6 md:mt-0 lg:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-2">
               {/* Left Column - Content */}
               <div className="text-center lg:text-left">
                  <div className="mb-6 inline-flex items-center rounded-full bg-orange-100 px-4 py-2 text-sm font-semibold text-orange-700">
                     <Star className="mr-2 h-4 w-4" />
                     FREE MEMBERSHIP
                  </div>
                  <h1 className="mb-6 text-4xl font-bold leading-tight text-gray-900 md:text-6xl">
                     Grow Your Restaurant Business with <span className="text-orange-500">Free Resources</span>
                  </h1>
                  <p className="mb-8 text-xl leading-relaxed text-gray-600 md:text-2xl">
                     Restaurant Association provides free tools, insights, and networking to help restaurant owners
                     innovate and succeed.
                  </p>

                  {/* Signup Form */}
                  <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-6 shadow-xl">
                     {!isSubmitted ? (
                        <form onSubmit={handleSubmit} className="space-y-4">
                           <div className="text-left">
                              <label htmlFor="email" className="mb-2 block text-sm font-semibold text-gray-700">
                                 Join 10,000+ Restaurant Professionals
                              </label>
                              <div className="flex flex-col gap-3 sm:flex-row">
                                 <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email address"
                                    className="flex-1 rounded-lg border border-gray-300 px-4 py-3 transition-all duration-200 focus:border-transparent focus:ring-2 focus:ring-orange-500"
                                    required
                                 />
                                 <button
                                    type="submit"
                                    className="flex transform items-center justify-center rounded-lg bg-orange-500 px-8 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:scale-105 hover:bg-orange-600"
                                 >
                                    Get My Free Account!
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                 </button>
                              </div>
                           </div>
                           <p className="text-left text-sm text-gray-500">
                              No credit card required. Instant access to all resources.
                           </p>
                        </form>
                     ) : (
                        <div className="py-4 text-center">
                           <CheckCircle className="mx-auto mb-3 h-12 w-12 text-green-500" />
                           <h3 className="mb-2 text-lg font-semibold text-gray-900">Welcome to the Association!</h3>
                           <p className="text-gray-600">Redirecting you to complete your membership...</p>
                        </div>
                     )}
                  </div>

                  <div className="flex cursor-pointer flex-col justify-center gap-4 sm:flex-row lg:justify-start">
                     <a
                        href={Routes.client.unifiedLogin.getPath()}
                        className="rounded-lg border-2 border-orange-500 px-8 py-3 text-lg font-semibold text-orange-500 transition-all duration-200 hover:bg-orange-500 hover:text-white"
                     >
                        Already a Member? Log In
                     </a>
                  </div>
               </div>

               {/* Right Column - Hero Image */}
               <div className="relative">
                  <div className="relative z-10">
                     <div className="relative h-96 w-full">
                        <OptimizedImage
                           fill
                           sizes="(min-width: 80rem) 616px, (min-width: 1024px) 48vw, 100vw"
                           src="/_images/ui/welcome-hero.jpg"
                           alt="Restaurant team collaboration"
                           fetchPriority="high"
                           className="absolute rounded-2xl object-cover shadow-2xl"
                        />
                     </div>
                     <div className="absolute -bottom-6 -left-6 rounded-xl border border-gray-100 bg-white p-4 shadow-lg">
                        <div className="flex items-center space-x-3">
                           <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-500">
                              <Users className="h-6 w-6 text-white" />
                           </div>
                           <div>
                              <p className="font-semibold text-gray-900">10,000+</p>
                              <p className="text-sm text-gray-600">Active Members</p>
                           </div>
                        </div>
                     </div>
                     <div className="absolute -right-6 -top-6 rounded-xl border border-gray-100 bg-white p-4 shadow-lg">
                        <div className="flex items-center space-x-3">
                           <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-500">
                              <Star className="h-6 w-6 text-white" />
                           </div>
                           <div>
                              <p className="font-semibold text-gray-900">100% Free</p>
                              <p className="text-sm text-gray-600">Always</p>
                           </div>
                        </div>
                     </div>
                  </div>
                  {/* Background decoration */}
                  <div className="absolute right-8 top-8 -z-10 h-32 w-32 rounded-full bg-orange-200 opacity-20"></div>
                  <div className="absolute bottom-8 left-8 -z-10 h-24 w-24 rounded-full bg-orange-200 opacity-20"></div>
               </div>
            </div>
         </div>
      </section>
   )
}

export default Hero
